import type { WithId } from '@medplum/core';
import type { Observation, Task } from '@medplum/fhirtypes';
import { isSlotItem, type DayItem } from './dayItem';

/** Shape of the `getDayTasks` query cache both apps update optimistically. Structurally
 *  matches each app's tRPC `getDayTasks` output; declared here so the cache surgery and
 *  the `DayItem` model live together. */
export interface DayTasksData {
  items: DayItem[];
  observations?: Record<string, WithId<Observation>>;
}

/** A toggle a `DayItem` can undergo, derived from the mutation that fired. Mirrors the
 *  four dispatch paths in each app's `useTaskToggle`. */
export type DayTaskToggleOp =
  | { type: 'completeSlot'; templateId: string; date: string; slotCode: string | null }
  | { type: 'uncompleteSlot'; occurrenceId: string }
  | { type: 'completeOneOff'; taskId: string }
  | { type: 'uncompleteOneOff'; taskId: string };

/** Prefix marking a placeholder occurrence id minted before the server has materialized the
 *  real one. Callers detect it to avoid firing an uncomplete against an id that doesn't exist
 *  on the server yet. */
export const OPTIMISTIC_OCCURRENCE_ID_PREFIX = 'optimistic-occurrence-';

/** A placeholder occurrence stamped onto a slot while its completion is in flight. It only
 *  needs to exist (and carry an id) for `isItemCompleted` and the card to render complete;
 *  the real occurrence replaces it via {@link resolveOptimisticSlot} on success (and on the
 *  settle invalidation). */
function optimisticOccurrence(templateId: string, date: string, slotCode: string | null): WithId<Task> {
  return {
    resourceType: 'Task',
    id: `${OPTIMISTIC_OCCURRENCE_ID_PREFIX}${templateId}-${date}-${slotCode ?? ''}`,
    status: 'completed',
    intent: 'order',
  };
}

/** Days-per-week rows carry a weekly count; keep it in step with the toggle so the
 *  "N of M" chip updates immediately. Absent on non-quota rows (spread of `{}`). */
function bumpWeeklyProgress(item: DayItem, delta: 1 | -1): Pick<DayItem, 'weeklyProgress'> | Record<string, never> {
  if (!item.weeklyProgress) return {};
  const completed = Math.max(0, item.weeklyProgress.completed + delta);
  return { weeklyProgress: { ...item.weeklyProgress, completed } };
}

function toggleItem(item: DayItem, op: DayTaskToggleOp): DayItem {
  switch (op.type) {
    case 'completeSlot':
      if (
        isSlotItem(item) &&
        item.task.id === op.templateId &&
        item.date === op.date &&
        (item.slotCode ?? null) === op.slotCode &&
        !item.completedOccurrence
      ) {
        return {
          ...item,
          completedOccurrence: optimisticOccurrence(op.templateId, op.date, op.slotCode),
          ...bumpWeeklyProgress(item, +1),
        };
      }
      return item;
    case 'uncompleteSlot':
      if (item.completedOccurrence?.id === op.occurrenceId) {
        return { ...item, completedOccurrence: undefined, ...bumpWeeklyProgress(item, -1) };
      }
      return item;
    case 'completeOneOff':
      if (!isSlotItem(item) && item.task.id === op.taskId) {
        return { ...item, task: { ...item.task, status: 'completed' } };
      }
      return item;
    case 'uncompleteOneOff':
      if (!isSlotItem(item) && item.task.id === op.taskId) {
        return { ...item, task: { ...item.task, status: 'ready' } };
      }
      return item;
    default:
      return op satisfies never;
  }
}

/** Apply a toggle to a cached `getDayTasks` page so the affected card flips immediately.
 *  Pure — returns a new object, or `undefined` unchanged for an unfetched cache entry.
 *  Callers snapshot the previous value first and restore it on error. */
export function applyOptimisticDayTaskToggle(
  data: DayTasksData | undefined,
  op: DayTaskToggleOp,
): DayTasksData | undefined {
  if (!data) return data;
  return { ...data, items: data.items.map((item) => toggleItem(item, op)) };
}

/** Replace a slot's placeholder occurrence with the real one returned by `completeSlot`, so a
 *  later uncomplete targets the actual occurrence id rather than the placeholder. Pure; leaves
 *  an unfetched (`undefined`) cache untouched.
 *
 *  When the slot has no occurrence at resolve time (the user unchecked while the
 *  completion was in flight, clearing the placeholder and decrementing the count),
 *  re-stamping the occurrence must also re-bump the weekly count — the deferred
 *  real uncomplete will decrement it again, and without the bump the chip would
 *  under-read until the settle invalidation. */
export function resolveOptimisticSlot(
  data: DayTasksData | undefined,
  slot: { templateId: string; date: string; slotCode: string | null },
  occurrence: WithId<Task>,
): DayTasksData | undefined {
  if (!data) return data;
  return {
    ...data,
    items: data.items.map((item) =>
      isSlotItem(item) &&
      item.task.id === slot.templateId &&
      item.date === slot.date &&
      (item.slotCode ?? null) === slot.slotCode
        ? {
            ...item,
            completedOccurrence: occurrence,
            ...(item.completedOccurrence ? {} : bumpWeeklyProgress(item, +1)),
          }
        : item,
    ),
  };
}
